import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVehicleYears } from '@/hooks/useVehicleData';
import { useVehiclePhoto } from '@/hooks/useVehiclePhoto';
import { Upload, X } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { VehicleMakeCombobox } from './VehicleMakeCombobox';
import { VehicleModelCombobox } from './VehicleModelCombobox';

type VehicleType = 'suv' | 'truck' | 'sxs' | 'atv' | 'dirt_bike' | 'other';

const vehicleTypeOptions: { value: VehicleType; label: string }[] = [
  { value: 'suv', label: 'SUV / Jeep' },
  { value: 'truck', label: 'Truck' },
  { value: 'sxs', label: 'Side-by-Side / UTV' },
  { value: 'atv', label: 'ATV / Quad' },
  { value: 'dirt_bike', label: 'Dirt Bike / Motorcycle' },
  { value: 'other', label: 'Other' },
];

interface Vehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  nickname?: string;
  modifications?: string;
  primary_photo_url?: string;
  is_featured?: boolean;
  vehicle_type?: VehicleType;
}

interface VehicleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  userId: string;
  vehicle?: Vehicle;
}

export const VehicleFormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  userId,
  vehicle,
}: VehicleFormDialogProps) => {
  const [year, setYear] = useState<string>(vehicle?.year?.toString() || '');
  const [make, setMake] = useState<string>(vehicle?.make || '');
  const [model, setModel] = useState<string>(vehicle?.model || '');
  const [trim, setTrim] = useState<string>(vehicle?.trim || '');
  const [nickname, setNickname] = useState<string>(vehicle?.nickname || '');
  const [modifications, setModifications] = useState<string>(vehicle?.modifications || '');
  const [vehicleType, setVehicleType] = useState<VehicleType | ''>(vehicle?.vehicle_type || '');
  const [photoUrl, setPhotoUrl] = useState<string>(vehicle?.primary_photo_url || '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const { data: years } = useVehicleYears();
  const { uploadVehiclePhoto, isUploading } = useVehiclePhoto();

  // Reset make/model when year changes
  useEffect(() => {
    if (!vehicle) {
      setMake('');
      setModel('');
    }
  }, [year, vehicle]);

  // Reset model when make changes
  useEffect(() => {
    if (!vehicle) {
      setModel('');
    }
  }, [make, vehicle]);

  useEffect(() => {
    if (vehicle) {
      setYear(vehicle.year.toString());
      setMake(vehicle.make);
      setModel(vehicle.model);
      setTrim(vehicle.trim || '');
      setNickname(vehicle.nickname || '');
      setModifications(vehicle.modifications || '');
      setVehicleType(vehicle.vehicle_type || '');
      setPhotoUrl(vehicle.primary_photo_url || '');
    }
  }, [vehicle]);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [selectedFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let uploadedPhotoUrl = photoUrl;

    // Upload photo if a new file was selected
    if (selectedFile) {
      const vehicleId = vehicle?.id || crypto.randomUUID();
      uploadedPhotoUrl = (await uploadVehiclePhoto(selectedFile, userId, vehicleId)) || '';
    }

    onSubmit({
      year: parseInt(year),
      make,
      model,
      trim,
      nickname,
      modifications,
      vehicle_type: vehicleType || null,
      primary_photo_url: uploadedPhotoUrl,
    });

    // Reset form
    if (!vehicle) {
      setYear('');
      setMake('');
      setModel('');
      setTrim('');
      setNickname('');
      setModifications('');
      setVehicleType('');
      setPhotoUrl('');
      setSelectedFile(null);
      setPreviewUrl('');
    }
  };

  const isFormValid = year && make && model;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{vehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
          <DialogDescription>
            {vehicle
              ? 'Update your vehicle information'
              : 'Add a new vehicle to your garage'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload */}
          <div className="space-y-2">
            <Label>Vehicle Photo</Label>
            {(previewUrl || photoUrl) ? (
              <div className="relative">
                <AspectRatio ratio={16 / 9}>
                  <img
                    src={previewUrl || photoUrl}
                    alt="Vehicle preview"
                    className="object-cover rounded-lg w-full h-full photo-signature"
                  />
                </AspectRatio>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl('');
                    setPhotoUrl('');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  id="vehicle-photo"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="vehicle-photo"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Click to upload a photo
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Vehicle Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year">Year *</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {years?.map((y) => (
                    <SelectItem key={y.id} value={y.name}>
                      {y.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="make">Make *</Label>
              <VehicleMakeCombobox
                year={year}
                value={make}
                onValueChange={setMake}
                disabled={!year}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model *</Label>
              <VehicleModelCombobox
                year={year}
                make={make}
                value={model}
                onValueChange={setModel}
                disabled={!make}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trim">Trim</Label>
              <Input
                id="trim"
                value={trim}
                onChange={(e) => setTrim(e.target.value)}
                placeholder="e.g., Rubicon, TRD Pro"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="e.g., Big Blue"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vehicle-type">Vehicle Type</Label>
            <Select value={vehicleType} onValueChange={(value) => setVehicleType(value as VehicleType)}>
              <SelectTrigger>
                <SelectValue placeholder="Select vehicle type" />
              </SelectTrigger>
              <SelectContent>
                {vehicleTypeOptions.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Used to categorize your vehicle for trail recommendations
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="modifications">Modifications</Label>
            <Textarea
              id="modifications"
              value={modifications}
              onChange={(e) => setModifications(e.target.value)}
              placeholder="Describe your mods and upgrades..."
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid || isUploading}>
              {isUploading ? 'Uploading...' : vehicle ? 'Save Changes' : 'Add Vehicle'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
